import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useManagersOverviewState } from "../../hooks/useOptimizedState";
import ManagerDetailDialog from "./popup";
import { ManagerOverviewStatCard } from "./ManagerOverviewStatCard";
import { getAccountingFundRows, buildManagersOverviewRows } from "./managerOverviewModel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import { formatValueDecimal } from "../../component/Methods";
import { formatSignedUsdCompact, formatSignedCurrency, formatAumMillions } from "./managerOverviewDisplayUtils";

const EMPTY_FUNDS = [];
const EMPTY_STRATEGIES = [];

/** Full AUM in dollars (2 dp) for table cell tooltips. */
function aumFullTooltip(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const core = formatValueDecimal(Math.abs(n));
  return n < 0 ? `-$${core}` : `$${core}`;
}

const ManagerOverviewDashboard = () => {
  const { currentTheme: theme } = useTheme();
  const navigate = useNavigate();
  const [detailRow, setDetailRow] = useState(null);
  const {
    managers,
    strategies,
    lots,
    accountingData,
    funds,
    livePrices,
    trades,
    markets,
    isStrategist,
  } = useManagersOverviewState();
  const managersForOverview = useMemo(() => (Array.isArray(managers) ? managers : []), [managers]);

  const accountingRowsAll = useMemo(
    () => getAccountingFundRows(accountingData ?? []),
    [accountingData]
  );

  const rows = useMemo(() => {
    const sList = strategies ?? [];
    const lList = lots ?? [];
    return buildManagersOverviewRows({
      managers: managersForOverview,
      strategies: sList,
      lots: lList,
      accountingRowsAll,
      accountingData: accountingData ?? [],
      funds: funds ?? [],
      livePrices: livePrices ?? [],
      trades: trades ?? [],
      markets: markets ?? [],
      isStrategist,
    });
  }, [
    managersForOverview,
    strategies,
    lots,
    accountingRowsAll,
    accountingData,
    funds,
    livePrices,
    trades,
    markets,
    isStrategist,
  ]);

  const totals = useMemo(() => {
    const totalFunds = rows.reduce((acc, r) => acc + r.fundCount, 0);
    const totalSmas = rows.reduce((acc, r) => acc + r.smaCount, 0);
    const totalStrategies = rows.reduce((acc, r) => acc + r.strategyCount, 0);
    const totalAum = rows.reduce((acc, r) => acc + (Number(r.aum) || 0), 0);
    const totalLots = rows.reduce((acc, r) => acc + r.lotCount, 0);
    const totalOpenLots = rows.reduce((acc, r) => acc + r.openLotCount, 0);
    return {
      managers: rows.length,
      totalFunds,
      totalSmas,
      totalStrategies,
      totalAum,
      totalLots,
      totalOpenLots,
    };
  }, [rows]);

  const headCellSx = {
    fontWeight: 600,
    fontSize: "12px",
    // Use app tokens — this project's dark theme inverts grey[n] vs MUI defaults (grey[800] is light).
    backgroundColor: theme.palette.background.tableHeader,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    whiteSpace: "nowrap",
    py: 1.25,
  };

  const bodyCellSx = {
    fontSize: "13px",
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    py: 1.25,
  };

  const pnlColor = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return theme.palette.text.secondary;
    if (n > 0) return theme.palette.success.main;
    return theme.palette.error.main;
  };

  /** Richer tooltips for compact money cells (full value on hover). */
  const numericDetailTooltipProps = useMemo(
    () => ({
      arrow: true,
      placement: "top",
      enterDelay: 350,
      slotProps: {
        tooltip: {
          sx: {
            px: 1.75,
            py: 1.125,
            maxWidth: 400,
            fontSize: "0.8125rem",
            fontWeight: 600,
            lineHeight: 1.45,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "0.02em",
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          },
        },
        arrow: {
          sx: {
            color: theme.palette.background.paper,
          },
        },
      },
    }),
    [theme]
  );

  return (
    <>
      <Helmet>
        <title>Managers overview | Vantage Alpha</title>
      </Helmet>

      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === "light" ? "#ebebeb" : theme.palette.grey[50],
          background:
            theme.palette.mode === "light" ? "#ebebeb" : theme.palette.grey[50],
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: 1640,
            mx: "auto",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Managers overview
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 900,
              }}
            >
              Executive view across every manager: <strong>AUM</strong> from accounting, book counts (funds,
              strategies, SMAs), and <strong>aggregated position P/L</strong> by week, month, quarter, and year
              (equity lots on each manager&apos;s book). Open <strong>Details</strong> for a full drill-down, or the
              performance dashboard for vantage metrics.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(6, minmax(0, 1fr))",
              },
            }}
          >
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="Accounting"
                subtitle="Total AUM"
                value={formatAumMillions(totals.totalAum)}
                icon={AttachMoneyOutlinedIcon}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="Position lots"
                subtitle={
                  totals.totalOpenLots === totals.totalLots
                    ? "Open (non-flat)"
                    : `${totals.totalOpenLots.toLocaleString()} open (non-flat)`
                }
                value={totals.totalLots.toLocaleString()}
                icon={ViewListOutlinedIcon}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="Managers"
                subtitle="In directory"
                value={totals.managers.toLocaleString()}
                icon={GroupsOutlinedIcon}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="Strategies"
                subtitle="Listed"
                value={totals.totalStrategies.toLocaleString()}
                icon={AccountTreeOutlinedIcon}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="Funds"
                subtitle="Total"
                value={totals.totalFunds.toLocaleString()}
                icon={PieChartOutlineIcon}
              />
            </Box>
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <ManagerOverviewStatCard
                theme={theme}
                title="SMAs"
                subtitle="Total"
                value={totals.totalSmas.toLocaleString()}
                icon={LayersOutlinedIcon}
              />
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: theme.palette.divider,
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Manager directory
              </Typography>
              <Typography variant="caption" color="text.secondary">
                AUM and period change are derived from accounting and position lots on each manager&apos;s book.
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: "auto", overflowY: "visible" }}>
              <Table size="small" sx={{ tableLayout: "auto" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Manager name</TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      AUM (M)
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Funds
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Strategies
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      SMAs
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Change (week)
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Change (month)
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Change (quarter)
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Change (year)
                    </TableCell>
                    <TableCell align="right" sx={headCellSx}>
                      Change (365 Days)
                    </TableCell>
                    <TableCell align="center" sx={headCellSx}>
                      Details
                    </TableCell>
                    <TableCell align="right" sx={{ ...headCellSx, minWidth: 200 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} sx={bodyCellSx}>
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No managers loaded yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.managerId || row.name} hover>
                        <TableCell sx={bodyCellSx}>
                          <Typography fontWeight={600}>{row.name}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }}>
                          <Tooltip {...numericDetailTooltipProps} title={aumFullTooltip(row.aum)}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                width: "100%",
                                textAlign: "right",
                                cursor: "default",
                              }}
                            >
                              {formatAumMillions(row.aum)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }}>
                          {row.fundCount}
                        </TableCell>
                        <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }}>
                          {row.strategyCount}
                        </TableCell>
                        <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }}>
                          {row.smaCount}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...bodyCellSx,
                            fontVariantNumeric: "tabular-nums",
                            color: pnlColor(row.week),
                          }}
                        >
                          <Tooltip {...numericDetailTooltipProps} title={formatSignedCurrency(row.week)}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                width: "100%",
                                textAlign: "right",
                                cursor: "default",
                              }}
                            >
                              {formatSignedUsdCompact(row.week)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...bodyCellSx,
                            fontVariantNumeric: "tabular-nums",
                            color: pnlColor(row.month),
                          }}
                        >
                          <Tooltip {...numericDetailTooltipProps} title={formatSignedCurrency(row.month)}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                width: "100%",
                                textAlign: "right",
                                cursor: "default",
                              }}
                            >
                              {formatSignedUsdCompact(row.month)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...bodyCellSx,
                            fontVariantNumeric: "tabular-nums",
                            color: pnlColor(row.quarter),
                          }}
                        >
                          <Tooltip {...numericDetailTooltipProps} title={formatSignedCurrency(row.quarter)}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                width: "100%",
                                textAlign: "right",
                                cursor: "default",
                              }}
                            >
                              {formatSignedUsdCompact(row.quarter)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...bodyCellSx,
                            fontVariantNumeric: "tabular-nums",
                            color: pnlColor(row.year),
                          }}
                        >
                          <Tooltip {...numericDetailTooltipProps} title={formatSignedCurrency(row.year)}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                width: "100%",
                                textAlign: "right",
                                cursor: "default",
                              }}
                            >
                              {formatSignedUsdCompact(row.year)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...bodyCellSx,
                            fontVariantNumeric: "tabular-nums",
                            color: pnlColor(row.days365),
                          }}
                        >
                          <Tooltip {...numericDetailTooltipProps} title={formatSignedCurrency(row.days365)}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                width: "100%",
                                textAlign: "right",
                                cursor: "default",
                              }}
                            >
                              {formatSignedUsdCompact(row.days365)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={bodyCellSx}>
                          <Tooltip title="Details">
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="View manager details"
                              onClick={() => setDetailRow(row)}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={bodyCellSx}>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                              onClick={() =>
                                navigate("/vantage", {
                                  state: { managerId: row.managerId, managerName: row.name },
                                })
                              }
                              sx={{
                                borderWidth: 1.5,
                                "&:hover": {
                                  borderWidth: 1.5,
                                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                  backgroundImage: "none",
                                },
                              }}
                            >
                              Performance dashboard
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <ManagerDetailDialog
            open={Boolean(detailRow)}
            onClose={() => setDetailRow(null)}
            row={detailRow}
            funds={funds ?? EMPTY_FUNDS}
            strategies={strategies ?? EMPTY_STRATEGIES}
          />
        </Container>
      </Box>
    </>
  );
};

export default ManagerOverviewDashboard;
